import integrationRoutes from '../helpers/routes';
import {
    IAccount,
    IAccountAndTaxMapping,
    IBilling,
    IInstallation,
    IIntegrationEvent,
    IntegrationType,
    IOrganisation,
    IShopifySettingsResponse,
    IUser,
    IXeroTaxRate,
} from '../models';
import { RootState } from '../redux/rootReducer';
import { IAccountSetupState } from '../redux/slices/AccountSetupSlice';
import { connectionTypes, installationStates, integrationEventNames, integrationTypes } from '../static/constants';

const tenantId = '7a8e7b5f-6b15-4db0-92d5-f774d230b276';
const tenantName = 'Gee Bagels';

export const headerDataMock = {
    header: {},
    headerAssets: {
        js: {},
        css: {},
    },
};

const baseEventMock = {
    eventDate: 0, // to prevent snapshot tests failing in UAT which is UTC vs browser time in local tests
    eventName: integrationEventNames.CONTACT_SENT,
    contactName: tenantName,
    userName: 'Zach Smith',
};

export const integrationEventDataMock = {
    events: [
        {
            ...baseEventMock,
            eventName: integrationEventNames.ERROR_CONNECTION,
        },
        {
            ...baseEventMock,
            eventName: integrationEventNames.ERROR_LIST_NOT_FOUND,
        },
        baseEventMock,
        baseEventMock,
        baseEventMock,
        baseEventMock,
        {
            ...baseEventMock,
            eventName: integrationEventNames.INTEGRATION_CONNECTED,
        },
    ] as IIntegrationEvent[],
};

export const customConnectionsInstallationMock: IInstallation = {
    allowSmartLists: true,
    installationId: 'some-random-guid',
    integrationType: integrationTypes.CUSTOM as IntegrationType,
    state: installationStates.HEALTHY,
    connectionType: connectionTypes.OAUTH2,
    displayName: 'Custom connection',
    authorizeUrl: '',
    signUpUrl: '',
    routes: integrationRoutes.get(integrationTypes.CUSTOM),
    isOnboarded: true,
    requiresHostname: false,
    billing: {
        hasFreePlan: false,
        hasPaidPlan: true,
        hasBought: false,
        url: 'https://apps.livestage.test.xero.com/123456/nz/subscription/customconnection/add',
        manageSubscriptionUrl: 'https://apps.livestage.test.xero.com/123456/my-apps/78910',
        nextBillingDate: null,
        subscriptionEndDate: null,
    },
    versionSupport: {
        canUpdate: false,
        currentVersion: 1,
        latestVersion: 1,
        updateRequiresAuthorization: false,
    },
};

export const hubspotInstallationMock: IInstallation = {
    displayName: 'HubSpot CRM integration',
    requiresHostname: false,
    signUpUrl: '',
    state: installationStates.HEALTHY,
    isOnboarded: false,
    installationId: 'some-random-guid',
    allowSmartLists: true,
    authorizeUrl: 'https://google.com',
    integrationType: 'HUBSPOT',
    connectionType: connectionTypes.OAUTH2,
    billing: {
        hasFreePlan: true,
        hasPaidPlan: true,
        hasBought: false,
        url: 'https://apps.livestage.test.xero.com/123456/nz/subscription/hubspot/add',
        manageSubscriptionUrl: 'https://apps.livestage.test.xero.com/123456/my-apps/78910',
        nextBillingDate: null,
        subscriptionEndDate: null,
    },
    versionSupport: {
        canUpdate: false,
        currentVersion: 2,
        latestVersion: 2,
        updateRequiresAuthorization: true,
    },
};

export const shopifyInstallationMock: IInstallation = {
    displayName: 'Shopify integration by Xero',
    requiresHostname: true,
    signUpUrl: 'https://signup.com',
    state: installationStates.HEALTHY,
    isOnboarded: false,
    installationId: 'some-random-guid',
    allowSmartLists: true,
    authorizeUrl: 'https://%s/authorize',
    integrationType: integrationTypes.SHOPIFY as IntegrationType,
    connectionType: connectionTypes.OAUTH2,
    billing: {
        hasFreePlan: false,
        hasPaidPlan: true,
        hasBought: false,
        url: 'https://apps.livestage.test.xero.com/123456/nz/subscription/shopify/add',
        manageSubscriptionUrl: 'https://apps.livestage.test.xero.com/123456/my-apps/78910',
        subscriptionEndDate: '2099-01-01T00:00:00Z',
        nextBillingDate: '2099-01-01T00:00:00Z',
    } as IBilling,
    freeTrial: {
        isEligibleForTrial: false,
        freeTrialDurationDays: 30,
        freeTrialEndDateUtc: '2021-11-08T00:00:00',
        freeTrialRemainingDays: 30,
    },
    versionSupport: {
        canUpdate: true,
        latestVersion: 2,
        currentVersion: 1,
        updateRequiresAuthorization: false,
    },
};

export const userMock: IUser = {
    given_name: 'Wally',
    family_name: 'West',
    email: 'wally.west@xero.com',
    sub: '',
    sid: '',
    auth_time: 1232,
    idp: '',
    preferred_username: 'Flash',
    xero_userid: '81b37c85-3ce0-4687-9cff-4945a965c166',
    amr: [],
};

export const userOrganisationsMock: IOrganisation[] = [
    {
        name: 'Gee Bagels',
        legalName: 'Gee Bagels',
        id: '35cf8364-75f4-499b-b12a-1ca83db1274e',
        userIsAdmin: true,
        hasInstallation: false,
        baseCurrency: 'NZD',
        class: 'PREMIUM',
        paysTax: true,
    },
];
export const organisationsMock = [
    {
        name: tenantName,
        legalName: tenantName,
        id: tenantId,
        userIsAdmin: true,
        baseCurrency: 'Nzd',
        paysTax: false,
        hasInstallation: false,
    },
    {
        name: 'Vandalay Industries',
        legalName: 'Georges lie',
        id: '691a7deb-c35b-4b0e-b207-7d120efc2756',
        userIsAdmin: true,
        baseCurrency: 'Aud',
        paysTax: false,
        hasInstallation: false,
    },
    {
        name: 'Tax registered org',
        legalName: 'Tax registered org',
        id: '691a7deb-c35b-4b0e-b207-7d120efc2757',
        userIsAdmin: true,
        baseCurrency: 'Nzd',
        paysTax: true,
        hasInstallation: false,
    },
    {
        name: 'Non-admin org',
        legalName: 'Non-admin org',
        id: '691a7deb-c35b-4b0e-b207-7d120efc2758',
        userIsAdmin: false,
        baseCurrency: 'Nzd',
        paysTax: false,
        hasInstallation: false,
    },
    {
        name: 'NZ non-tax org',
        legalName: 'NZ non-tax org',
        id: '691a7deb-c35b-4b0e-b207-7d120efc2759',
        userIsAdmin: true,
        baseCurrency: 'Nzd',
        paysTax: false,
        hasInstallation: false,
    },
    {
        name: 'NZ connected org',
        legalName: 'NZ connected org',
        id: '691a7deb-c35b-4b0e-b207-7d120efc2760',
        userIsAdmin: true,
        baseCurrency: 'Nzd',
        paysTax: false,
        hasInstallation: true,
    },
    {
        name: 'Tax registered org 2',
        legalName: 'Tax registered org 2',
        id: '691a7deb-c35b-4b0e-b207-7d120efc2789',
        userIsAdmin: true,
        baseCurrency: 'Nzd',
        paysTax: true,
        hasInstallation: false,
    },
];

export const accountSetupStateMock: IAccountSetupState = {
    isCreating: false,
};

export const shopifySettingsMock: IShopifySettingsResponse = {
    accountAndTaxMappings: [],
    paysTax: true,
    installationId: shopifyInstallationMock.installationId,
    bankAccounts: [
        {
            accountId: '267e8faa-c5da-4a1d-9347-36aebc0e27f4',
            bankAccountNumber: '12-3232-32-32322',
            code: '123',
            description: 'cloverfeild paradox',
            name: 'testing bank account',
            currencyCode: 'USD',
            class: 'REVENUE',
        },
        {
            accountId: '979gd7aa-t8se-100a-1234-74aubc0e482e',
            code: '123',
            name: 'testing clearing account',
            description: 'this is a description',
            bankAccountNumber: '56-1234-10-12222',
            currencyCode: 'NZD',
            class: 'BANK',
        },
    ],
    recommendedClearingAccountName: 'Shopify clearing account',
    paymentGateways: [
        {
            code: 'shopify_pay',
            name: 'Shopify Payments',
            used: true,
            reconcilable: true,
            isPopular: true,
        },
    ],
    shop: 'thisistotallyarealshop.myshopify.com',
    shopDetails: {
        currencyCode: 'Nzd',
    },
    chartOfAccounts: [],
    xeroTaxRates: [],
    chartOfAccountsSettings: [],
    contactId: null,
    syncFromDateUtc: null,
    syncToDateUtc: null,
    paymentGatewayDetails: {
        shopify_pay: {
            createInvoice: true,
            bankAccountCurrency: 'USD',
            bankAccountId: '267e8faa-c5da-4a1d-9347-36aebc0e27f4',
            clearingAccountCurrency: 'NZD',
            clearingAccountId: '979gd7aa-t8se-100a-1234-74aubc0e482e',
        },
    },
    xeroRevenueTaxRates: null,
    xeroRegion: 'NZ',
    taxAuthorities: null,
    shopifyTaxRegions: null,
    supportsTaxCollection: false,
};

export const storeDataMock: RootState = {
    bootstrap: {
        hasRequiredPlan: true,
        isAdmin: true,
        isAllowed: true,
        isBootstrapping: false,
    },
    clearingAccount: accountSetupStateMock,
    event: {
        events: integrationEventDataMock.events,
        isFetching: false,
    },
    installation: {
        installation: shopifyInstallationMock,
        isSyncing: false,
        isFetching: false,
        isDeleting: false,
    },
    organisation: {
        organisation: userOrganisationsMock[0],
        organisations: userOrganisationsMock,
    },
    settings: {
        settings: shopifySettingsMock,
        isSyncing: false,
        isFetching: false,
    },
    user: {
        user: userMock,
    },
    connection: {
        isConnecting: false,
        isDisconnecting: false,
    },
    freeTrial: {
        isStartingTrial: false,
    },
};

// based on local mocks
export const chartOfAccountsMock: IAccount[] = [
    {
        accountId: 'a2016f1a-c2df-443c-92ff-1a8f734d4499',
        code: '306',
        description: 'Do you like it?',
        name: 'Revenue account 3',
        class: 'REVENUE',
    },
    {
        accountId: 'db90b2dc-3c2f-4d80-a940-f2e61ccf058a',
        code: '302',
        description: 'Do you like it?',
        name: 'Some expense account',
        class: 'EXPENSE',
    },
    {
        accountId: '2b886225-8146-49c4-8714-9cec910d28dd',
        code: '301',
        description: 'Do you like it?',
        name: 'Some Other current liability account',
        class: 'LIABILITY',
    },
    {
        accountId: '55f9f0de-323b-4bc8-907a-f37a63fb065f',
        code: '305',
        description: 'Do you like it?',
        name: 'Revenue account 2',
        class: 'REVENUE',
    },
    {
        accountId: '837e1fdc-29d6-4ed4-a570-84bded1beee5',
        code: '300',
        description: 'Do you like it?',
        name: 'Some current liability account',
        class: 'LIABILITY',
    },
    {
        accountId: '5c39602f-0df2-48c3-bbaa-e30f67888660',
        code: '304',
        description: 'Do you like it?',
        name: 'Revenue account 1',
        class: 'REVENUE',
    },
    {
        accountId: 'd6738353-f2b6-4e0d-8dd6-dadfa855397d',
        code: '303',
        description: 'Do you like it?',
        name: 'Some Other expense account',
        class: 'EXPENSE',
    },
    {
        accountId: 'f6faf7da-817a-4670-aa82-b2426da4db17',
        code: '307',
        description: 'Do you like it?',
        name: 'Revenue account 4',
        class: 'REVENUE',
    },
];

// based on local mocks
export const xeroTaxRatesMock: IXeroTaxRate[] = [
    {
        taxComponents: [
            {
                name: 'GST',
                rate: 15,
            },
        ],
        displayTaxRate: 15,
        name: '15% GST on Expenses',
        taxType: 'INPUT2',
        canApplyToAssets: false,
        canApplyToEquity: false,
        canApplyToExpenses: true,
        canApplyToLiabilities: false,
        canApplyToRevenue: true,
    },
    {
        taxComponents: [
            {
                name: 'GST',
                rate: 0,
            },
        ],
        displayTaxRate: 0,
        name: 'GST on Imports',
        taxType: 'GSTONIMPORTS',
        canApplyToAssets: false,
        canApplyToEquity: false,
        canApplyToExpenses: true,
        canApplyToLiabilities: false,
        canApplyToRevenue: true,
    },
    {
        taxComponents: [
            {
                name: 'GST',
                rate: 15,
            },
        ],
        displayTaxRate: 15,
        name: '15% GST on Income',
        taxType: 'OUTPUT2',
        canApplyToAssets: false,
        canApplyToEquity: false,
        canApplyToExpenses: false,
        canApplyToLiabilities: false,
        canApplyToRevenue: true,
    },
    {
        taxComponents: [
            {
                name: 'GST',
                rate: 0,
            },
        ],
        displayTaxRate: 0,
        name: 'No GST',
        taxType: 'NONE',
        canApplyToAssets: true,
        canApplyToEquity: true,
        canApplyToExpenses: true,
        canApplyToLiabilities: true,
        canApplyToRevenue: true,
    },
    {
        taxComponents: [
            {
                name: 'GST',
                rate: 0,
            },
        ],
        displayTaxRate: 0,
        name: 'Zero Rated',
        taxType: 'ZERORATED',
        canApplyToAssets: true,
        canApplyToEquity: true,
        canApplyToExpenses: false,
        canApplyToLiabilities: true,
        canApplyToRevenue: true,
    },
];

export const initialAccountAndTaxMappingsMock: IAccountAndTaxMapping[] = [
    {
        region: 'NZ',
        transactionType: 'Gift cards',
        useRecommendedAccount: false,
    },
    {
        region: 'NZ',
        transactionType: 'Transaction fees',
        useRecommendedAccount: false,
    },
    {
        region: 'NZ',
        shopifyTaxRate: {
            countryCode: 'NZ',
            ratePercentage: 15,
            title: 'GST',
        },
        transactionType: 'Refunds',
        useRecommendedAccount: false,
    },
    {
        region: 'NZ',
        shopifyTaxRate: {
            countryCode: 'NZ',
            ratePercentage: 15,
            title: 'GST',
        },
        transactionType: 'Sales',
        useRecommendedAccount: false,
    },
    {
        region: 'NZ',
        shopifyTaxRate: {
            countryCode: 'NZ',
            ratePercentage: 15,
            title: 'GST',
        },
        transactionType: 'Shipping revenue',
        useRecommendedAccount: false,
    },
    {
        region: 'NZ',
        shopifyTaxRate: {
            countryCode: 'NZ',
            ratePercentage: 0,
            title: 'GST',
        },
        transactionType: 'Sales',
        useRecommendedAccount: false,
    },
    {
        region: 'NZ',
        shopifyTaxRate: {
            countryCode: 'NZ',
            ratePercentage: 0,
            title: 'GST',
        },
        transactionType: 'Refunds',
        useRecommendedAccount: false,
    },
    {
        region: 'NZ',
        transactionType: 'Sales - Tax exempt',
        useRecommendedAccount: false,
    },
    {
        region: 'NZ',
        transactionType: 'Refunds - Tax exempt',
        useRecommendedAccount: false,
    },
    {
        region: 'Global',
        transactionType: 'Gift cards',
        useRecommendedAccount: false,
    },
    {
        region: 'Global',
        transactionType: 'Transaction fees',
        useRecommendedAccount: false,
    },
    {
        region: 'Global',
        transactionType: 'Refunds',
        useRecommendedAccount: false,
    },
    {
        region: 'Global',
        transactionType: 'Sales',
        useRecommendedAccount: false,
    },
    {
        region: 'Global',
        transactionType: 'Shipping revenue',
        useRecommendedAccount: false,
    },
    {
        region: 'Global',
        transactionType: 'Transaction fees - Tax exempt',
        useRecommendedAccount: false,
    },
    {
        region: 'Global',
        transactionType: 'Tax collected - global',
        useRecommendedAccount: false,
    },
];

export const initialAccountAndTaxMappingsWithRecommendationsMock: IAccountAndTaxMapping[] = [
    {
        region: 'NZ',
        transactionType: 'Gift cards',
        useRecommendedAccount: true,
        xeroAccountId: '00000000-0000-0000-0000-000000000000',
        xeroTaxType: 'NONE',
        recommendedAccount: {
            code: '807',
            description: 'Shopify gift cards',
            accountId: '00000000-0000-0000-0000-000000000000',
            name: 'Shopify gift cards',
            class: 'LIABILITY',
        },
    },
    {
        region: 'NZ',
        transactionType: 'Transaction fees',
        useRecommendedAccount: true,
        xeroAccountId: '00000000-0000-0000-0000-000000000000',
        xeroTaxType: 'INPUT2',
        recommendedAccount: {
            code: '495',
            description: 'Shopify fees',
            accountId: '00000000-0000-0000-0000-000000000000',
            name: 'Shopify fees',
            class: 'EXPENSE',
        },
    },
    {
        region: 'NZ',
        shopifyTaxRate: {
            countryCode: 'NZ',
            ratePercentage: 15,
            title: 'GST',
        },
        transactionType: 'Refunds',
        useRecommendedAccount: true,
        xeroAccountId: '00000000-0000-0000-0000-000000000000',
        xeroTaxType: 'OUTPUT2',
        recommendedAccount: {
            code: '230',
            description: 'Shopify refunds',
            accountId: '00000000-0000-0000-0000-000000000000',
            name: 'Shopify refunds',
            class: 'REVENUE',
        },
    },
    {
        region: 'NZ',
        shopifyTaxRate: {
            countryCode: 'NZ',
            ratePercentage: 15,
            title: 'GST',
        },
        transactionType: 'Sales',
        useRecommendedAccount: true,
        xeroAccountId: '00000000-0000-0000-0000-000000000000',
        xeroTaxType: 'OUTPUT2',
        recommendedAccount: {
            code: '210',
            description: 'Shopify sales',
            accountId: '00000000-0000-0000-0000-000000000000',
            name: 'Shopify sales',
            class: 'REVENUE',
        },
    },
    {
        region: 'NZ',
        shopifyTaxRate: {
            countryCode: 'NZ',
            ratePercentage: 15,
            title: 'GST',
        },
        transactionType: 'Shipping revenue',
        useRecommendedAccount: true,
        xeroAccountId: '00000000-0000-0000-0000-000000000000',
        xeroTaxType: 'OUTPUT2',
        recommendedAccount: {
            code: '220',
            description: 'Shopify shipping revenue',
            accountId: '00000000-0000-0000-0000-000000000000',
            name: 'Shopify shipping revenue',
            class: 'REVENUE',
        },
    },
    {
        region: 'NZ',
        shopifyTaxRate: {
            countryCode: 'NZ',
            ratePercentage: 0,
            title: 'GST',
        },
        transactionType: 'Sales',
        useRecommendedAccount: false,
        recommendedAccount: {
            code: '210',
            description: 'Shopify sales',
            accountId: '00000000-0000-0000-0000-000000000000',
            name: 'Shopify sales',
            class: 'REVENUE',
        },
    },
    {
        region: 'NZ',
        shopifyTaxRate: {
            countryCode: 'NZ',
            ratePercentage: 0,
            title: 'GST',
        },
        transactionType: 'Refunds',
        useRecommendedAccount: false,
        recommendedAccount: {
            code: '230',
            description: 'Shopify refunds',
            accountId: '00000000-0000-0000-0000-000000000000',
            name: 'Shopify refunds',
            class: 'REVENUE',
        },
    },
    {
        region: 'NZ',
        transactionType: 'Sales - Tax exempt',
        useRecommendedAccount: false,
        recommendedAccount: {
            code: '210',
            description: 'Shopify sales',
            accountId: '00000000-0000-0000-0000-000000000000',
            name: 'Shopify sales',
            class: 'REVENUE',
        },
    },
    {
        region: 'NZ',
        transactionType: 'Refunds - Tax exempt',
        useRecommendedAccount: false,
        recommendedAccount: {
            code: '230',
            description: 'Shopify refunds',
            accountId: '00000000-0000-0000-0000-000000000000',
            name: 'Shopify refunds',
            class: 'REVENUE',
        },
    },
    {
        region: 'Global',
        transactionType: 'Gift cards',
        useRecommendedAccount: true,
        xeroAccountId: '00000000-0000-0000-0000-000000000000',
        xeroTaxType: 'NONE',
        recommendedAccount: {
            code: '807',
            description: 'Shopify gift cards',
            accountId: '00000000-0000-0000-0000-000000000000',
            name: 'Shopify gift cards',
            class: 'LIABILITY',
        },
    },
    {
        region: 'Global',
        transactionType: 'Transaction fees',
        useRecommendedAccount: true,
        xeroAccountId: '00000000-0000-0000-0000-000000000000',
        xeroTaxType: 'INPUT2',
        recommendedAccount: {
            code: '495',
            description: 'Shopify fees',
            accountId: '00000000-0000-0000-0000-000000000000',
            name: 'Shopify fees',
            class: 'EXPENSE',
        },
    },
    {
        region: 'Global',
        transactionType: 'Refunds',
        useRecommendedAccount: true,
        xeroAccountId: '00000000-0000-0000-0000-000000000000',
        xeroTaxType: 'NONE',
        recommendedAccount: {
            code: '230',
            description: 'Shopify refunds',
            accountId: '00000000-0000-0000-0000-000000000000',
            name: 'Shopify refunds',
            class: 'REVENUE',
        },
    },
    {
        region: 'Global',
        transactionType: 'Sales',
        useRecommendedAccount: true,
        xeroAccountId: '00000000-0000-0000-0000-000000000000',
        xeroTaxType: 'NONE',
        recommendedAccount: {
            code: '210',
            description: 'Shopify sales',
            accountId: '00000000-0000-0000-0000-000000000000',
            name: 'Shopify sales',
            class: 'REVENUE',
        },
    },
    {
        region: 'Global',
        transactionType: 'Shipping revenue',
        useRecommendedAccount: true,
        xeroAccountId: '00000000-0000-0000-0000-000000000000',
        xeroTaxType: 'OUTPUT2',
        recommendedAccount: {
            code: '220',
            description: 'Shopify shipping revenue',
            accountId: '00000000-0000-0000-0000-000000000000',
            name: 'Shopify shipping revenue',
            class: 'REVENUE',
        },
    },
    {
        region: 'Global',
        transactionType: 'Transaction fees - Tax exempt',
        useRecommendedAccount: false,
        recommendedAccount: {
            code: '495',
            description: 'Shopify fees',
            accountId: '00000000-0000-0000-0000-000000000000',
            name: 'Shopify fees',
            class: 'EXPENSE',
        },
    },
    {
        region: 'Global',
        transactionType: 'Tax collected - global',
        useRecommendedAccount: false,
    },
];

export const accountAndTaxMappingsMockWithSelections: IAccountAndTaxMapping[] = [
    {
        region: 'NZ',
        transactionType: 'Gift cards',
        xeroAccountId: 'a2016f1a-c2df-443c-92ff-1a8f734d4499', // class = REVENUE
        xeroTaxType: 'OUTPUT2', // canApplyToRevenue
        useRecommendedAccount: false,
    },
    {
        region: 'NZ',
        transactionType: 'Transaction fees',
        xeroAccountId: 'db90b2dc-3c2f-4d80-a940-f2e61ccf058a', // class = EXPENSE
        xeroTaxType: 'GSTONIMPORTS', // canApplyToExpenses
        useRecommendedAccount: false,
    },
    {
        region: 'NZ',
        shopifyTaxRate: {
            countryCode: 'NZ',
            ratePercentage: 15,
            title: 'GST',
        },
        transactionType: 'Refunds',
        xeroAccountId: '2b886225-8146-49c4-8714-9cec910d28dd',
        useRecommendedAccount: false,
    },
    {
        region: 'NZ',
        shopifyTaxRate: {
            countryCode: 'NZ',
            ratePercentage: 15,
            title: 'GST',
        },
        transactionType: 'Sales',
        useRecommendedAccount: false,
    },
    {
        region: 'Global',
        transactionType: 'Transaction fees',
        xeroAccountId: 'd6738353-f2b6-4e0d-8dd6-dadfa855397d',
        useRecommendedAccount: false,
    },
    {
        region: 'Global',
        transactionType: 'Refunds',
        xeroTaxType: 'GSTONIMPORTS',
        useRecommendedAccount: false,
    },
];

export const invalidTaxMappingsSelectAccountSelectTaxTate: IAccountAndTaxMapping[] = [
    {
        region: 'NZ',
        transactionType: 'Gift cards',
        xeroAccountId: 'a2016f1a-c2df-443c-92ff-1a8f734d4499',
        xeroTaxType: 'OUTPUT2',
        useRecommendedAccount: false,
    },
    {
        region: 'NZ',
        transactionType: 'Transaction fees',
        xeroAccountId: 'Doesnt exist in allChartOfAccounts',
        xeroTaxType: 'Doesnt exist in allTaxRates ',
        useRecommendedAccount: false,
    },
];

export const invalidTaxMappingsTaxRateMustMatchAccountType: IAccountAndTaxMapping[] = [
    {
        region: 'NZ',
        transactionType: 'Gift cards',
        xeroAccountId: 'a2016f1a-c2df-443c-92ff-1a8f734d4499',
        xeroTaxType: 'OUTPUT2',
        useRecommendedAccount: false,
    },
    {
        region: 'NZ',
        transactionType: 'Transaction fees',
        xeroAccountId: 'db90b2dc-3c2f-4d80-a940-f2e61ccf058a', // EXPENSE account
        xeroTaxType: 'OUTPUT2', // REVENUE
        useRecommendedAccount: false,
    },
];

export const taxMappingsWithCreatedRecommendedAccount: IAccountAndTaxMapping[] = [
    {
        region: 'Global',
        transactionType: 'Sales',
        useRecommendedAccount: false,
        recommendedAccount: {
            code: '210',
            description: 'Shopify sales',
            accountId: 'db90b2dc-3c2f-4d80-a940-f2e61ccf058a',
            name: 'Shopify sales',
            class: 'REVENUE',
        },
    },
];
